// The files behind a video: what is currently registered, how to add one, and
// how to swap one.

import React from 'react';
import { Alert, Box, TextField, Typography } from '@mui/material';
import VideoUploader from '../../components/VideoUploader';
import ReplaceVideoFile from '../../components/ReplaceVideoFile';
import { useAdmin } from '../AdminStore';
import { describeError, getVideo, patchVideo } from '../api';
import { videoIdFromAssets } from '../../components/videoUpload';
import UploadStatus from '../UploadStatus';

function AssetRow({ label, url }) {
  return (
    <Box sx={{ mb: '10px' }}>
      <span className="admin-label">{label}</span>
      <TextField
        fullWidth
        size="small"
        value={url || ''}
        InputProps={{ readOnly: true }}
        sx={{ mt: '4px' }}
      />
    </Box>
  );
}

export function MediaTab({ identifier, video }) {
  const { applyRecord, toast } = useAdmin();

  const apiVideoId = videoIdFromAssets(video.assets);

  // The uploader reports assets as soon as api.video can serve them. Reading
  // the record back first means a vertical upload cannot clobber the
  // horizontal assets that another tab may have just written.
  const onUploadSuccess = async (assets, uploadType) => {
    try {
      const current = await getVideo(identifier).catch(() => video);
      const patch =
        uploadType === 'vertical'
          ? {
              verticalAssets: { hls: assets.hls, mp4: assets.mp4 },
              verticalThumbnailUrl: assets.thumbnail,
            }
          : {
              assets: { hls: assets.hls, mp4: assets.mp4 },
              // Only seed the poster if there is not one already -- a hand-set
              // poster should survive an upload.
              ...(current.thumbnailUrl ? {} : { thumbnailUrl: assets.thumbnail }),
            };

      applyRecord(identifier, await patchVideo(identifier, patch));
      toast(`${uploadType} video registered.`, 'success');
    } catch (error) {
      toast(describeError(error, 'The file uploaded but the record was not updated.'), 'error');
    }
  };

  return (
    <Box sx={{ maxWidth: 720 }}>
      <span className="admin-label">Horizontal assets</span>
      {apiVideoId && (
        <Typography className="admin-mono" sx={{ fontSize: 11, color: 'var(--ps-text-dim)', mb: '10px' }}>
          api.video id {apiVideoId}
        </Typography>
      )}
      <Box sx={{ mt: '6px' }}>
        <AssetRow label="HLS" url={video.assets?.hls} />
        <AssetRow label="MP4" url={video.assets?.mp4} />
      </Box>

      {video.verticalAssets && (
        <>
          <hr className="admin-rule" />
          <span className="admin-label">Vertical assets</span>
          <Alert severity="info" sx={{ my: '10px' }}>
            Nothing on the site plays these yet — the player always uses the horizontal
            file. They are stored, not used.
          </Alert>
          <AssetRow label="Vertical HLS" url={video.verticalAssets?.hls} />
          <AssetRow label="Vertical MP4" url={video.verticalAssets?.mp4} />
        </>
      )}

      <hr className="admin-rule" />

      {/* Above the controls, and fed from the store: an upload started here
          keeps reporting after this tab has been unmounted and remounted, and
          so does one started by the replace dialog after it has closed. */}
      <UploadStatus identifier={identifier} />

      <VideoUploader
        identifier={identifier}
        hasHorizontal={!!video.assets?.mp4}
        onUploadSuccess={onUploadSuccess}
      />

      <hr className="admin-rule" />

      <ReplaceVideoFile
        videoData={video}
        videoIdentifier={identifier}
        onUpdateVideoData={(record) => applyRecord(identifier, record)}
      />
    </Box>
  );
}

export default MediaTab;
